import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import './UploadForm.css';
import PageLayout from '../PageLayout/PageLayout';
import ProcessingStepper from '../ProcessingStepper/ProcessingStepper';
import {
  uploadReports,
  MultiUploadTask,
  PerFileProgress,
} from '../../utils/s3Upload';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  useReportStatus,
  useBeginProcessing,
  useCreateReport,
  reportKeys,
} from '../../hooks/useReports';
import { parseError, ERROR_CODES } from '../../utils/errorHandlers';
import { FILE_SIZE, FILE_TYPES, HTTP_STATUS, US_STATES } from '../../constants';
import { ApiError } from '../../services/reportsApi';
import { useAccount, accountKeys } from '../../hooks/useAccount';
import { purchaseOptions, hasNoRuns, PurchaseOptions } from '../../utils/purchaseOptions';

/**
 * Copy for the up-front zero-runs card, checked in a fixed order. The first
 * arm matters most: a paying subscriber is never told purchasing is
 * unavailable, because their runs simply return at renewal.
 */
function noRunsCopy(options: PurchaseOptions): { message: string; linkText: string } {
  if (options.renewalOnly) {
    return {
      message: "You've used this period's plan runs. They renew at your next billing date.",
      linkText: 'Go to your account',
    };
  }
  if (options.canPurchase && options.plans.length > 0) {
    return {
      message:
        "You don't have any inspection runs available. Buy a credit pack or choose a subscription plan to run a report.",
      linkText: 'See plans and credits',
    };
  }
  if (options.canPurchase) {
    return {
      message: "You're out of inspection runs. Buy a credit pack to run another report.",
      linkText: 'Buy credits',
    };
  }
  return {
    message:
      "You don't have any inspection runs available. Purchasing isn't open yet; contact PropWise to add runs.",
    linkText: 'Go to your account',
  };
}

/** Shown when status polling outlives CACHE_TIME.STATUS_POLL_DEADLINE_MS. */
const POLL_TIMEOUT_MESSAGE =
  'This is taking longer than expected. Your report may still be processing; check My Reports for its status.';

/** An uncoded begin-processing 400: the files did not land where the API looks. */
const BEGIN_PROCESSING_REJECTED_MESSAGE =
  "Your upload didn't come through, so processing didn't start and no run was charged. Please submit the report again.";

/** Why the processing screen stopped polling, and what to offer next. */
interface PollFailure {
  message: string;
  /** A 401, or a bare 403: offer sign-in rather than a page that will fail too. */
  sessionExpired: boolean;
}

export interface FileValidationResult {
  /** The files that should be selected after this batch (unchanged from `current` when the batch is rejected). */
  nextFiles: File[];
  /** Human-readable rejection messages, in the order they occurred; each is fired as one toast. */
  errors: string[];
  /** True when the whole incoming batch is rejected by the aggregate total-size cap. */
  rejectedBatch: boolean;
}

/**
 * Pure validation of an incoming file batch against the current selection.
 * No React state, no toasts, no side effects — safe to call from a render,
 * an event handler, or a unit test. Reproduces the exact check order and
 * control flow of the original in-updater validation:
 *   PDF-type → per-file size (continue) → max-count (break) →
 *   duplicate-name (continue) → aggregate total-size (rejects whole batch).
 */
export function validateIncomingFiles(
  current: File[],
  incoming: File[]
): FileValidationResult {
  const nextFiles = [...current];
  const errors: string[] = [];
  for (const f of incoming) {
    if (f.type !== FILE_TYPES.PDF) {
      errors.push(`${f.name}: only PDF files are allowed.`);
      continue;
    }
    if (f.size > FILE_SIZE.MAX_UPLOAD_BYTES) {
      errors.push(`${f.name}: file exceeds ${FILE_SIZE.MAX_UPLOAD_MB}MB limit.`);
      continue;
    }
    if (nextFiles.length >= FILE_SIZE.MAX_FILES_PER_REPORT) {
      errors.push(`Maximum ${FILE_SIZE.MAX_FILES_PER_REPORT} files per report.`);
      break;
    }
    // Two files with the same name produce the same S3 key and would
    // silently overwrite each other. Reject by name regardless of size.
    if (nextFiles.some(x => x.name === f.name)) {
      errors.push(
        `A file named "${f.name}" is already selected. Rename one before adding.`
      );
      continue;
    }
    nextFiles.push(f);
  }
  const total = nextFiles.reduce((s, f) => s + f.size, 0);
  if (total > FILE_SIZE.MAX_TOTAL_UPLOAD_BYTES) {
    errors.push(
      `Total upload size exceeds ${FILE_SIZE.MAX_TOTAL_UPLOAD_MB}MB limit.`
    );
    return { nextFiles: current, errors, rejectedBatch: true };
  }
  return { nextFiles, errors, rejectedBatch: false };
}

const UploadForm: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTaskRef = useRef<MultiUploadTask | null>(null);
  const { showError, showSuccess, showWarning } = useToast();
  const { logout, recheckTerms } = useAuth();
  const beginProcessingMutation = useBeginProcessing();
  const createReportMutation = useCreateReport();
  const { data: account } = useAccount();
  // Advice only: the form stays usable, and the reactive denial banner below
  // remains the backstop. Nothing shows while the account is loading or failed.
  const showNoRunsCard = !!account && hasNoRuns(account);
  const options = account ? purchaseOptions(account) : null;
  const noRuns = showNoRunsCard && options ? noRunsCopy(options) : null;

  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    files: [] as File[],
    // Contract fields
    contractAcceptanceDate: '',
    inspectionWindowDays: '',
    isAsIs: false,
    repairCreditAmount: '',
    sellerRepairCreditAgreed: false,
    singleDefectThreshold: '',
    categoryThreshold: ''
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadStarted, setUploadStarted] = useState(false);
  const [perFileProgress, setPerFileProgress] = useState<PerFileProgress>({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [wasCancelled, setWasCancelled] = useState(false);
  const [entitlementDenialMessage, setEntitlementDenialMessage] = useState<string | null>(null);

  // Status polling state
  const [uploadedReportId, setUploadedReportId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollFailure, setPollFailure] = useState<PollFailure | null>(null);

  // Use status polling hook
  const {
    data: reportStatus,
    error: pollError,
    stopReason: pollStopReason,
  } = useReportStatus(uploadedReportId || undefined, isPolling);

  // Handle status updates - auto-navigate when complete
  useEffect(() => {
    if (reportStatus?.status === 'completed' || reportStatus?.status === 'failed') {
      setIsPolling(false);

      // Invalidate reports cache so My Reports list refreshes
      queryClient.invalidateQueries({ queryKey: reportKeys.all });

      // Surface a clear, distinct message when processing fails — otherwise the
      // failure is silent (and a stale banner could misattribute the cause).
      if (reportStatus.status === 'failed') {
        showError(
          'Report processing failed. Please try uploading again, or contact PropWise if the problem persists.'
        );
      }

      // No mounted check: effects never run after unmount, and a ref set false
      // in cleanup stays false after StrictMode's dev double mount (FE-UI-05).
      if (uploadedReportId) {
        navigate(`/report/${encodeURIComponent(uploadedReportId)}`, {
          state: { fromUpload: true }
        });
      }
    }
  }, [reportStatus?.status, uploadedReportId, navigate, queryClient, showError]);

  // Polling gave up before the report settled: an error polling cannot fix
  // (an expired session, a missing report) or the overall deadline. Swap the
  // spinner for what happened and where to go, instead of spinning forever.
  useEffect(() => {
    if (!isPolling || !pollStopReason) return;
    setIsPolling(false);
    if (pollStopReason === 'timeout') {
      setPollFailure({ message: POLL_TIMEOUT_MESSAGE, sessionExpired: false });
      return;
    }
    const appError = parseError(pollError);
    setPollFailure({
      message: appError.userMessage,
      sessionExpired: appError.code === ERROR_CODES.UNAUTHORIZED,
    });
  }, [isPolling, pollStopReason, pollError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (entitlementDenialMessage) setEntitlementDenialMessage(null);
  };

  const addFiles = (incoming: File[]) => {
    const { nextFiles, errors, rejectedBatch } = validateIncomingFiles(
      formData.files,
      incoming
    );
    if (!rejectedBatch) {
      setFormData(prev => ({ ...prev, files: nextFiles }));
      if (wasCancelled) setWasCancelled(false);
    }
    errors.forEach(showError);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    addFiles(incoming);
    // Reset input so the same filename can be re-added after removal.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = (name: string, size: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter(f => !(f.name === name && f.size === size)),
    }));
    setPerFileProgress(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const incoming = Array.from(e.dataTransfer.files || []);
    if (incoming.length === 0) return;
    addFiles(incoming);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    setWasCancelled(false);
    // Clear any denial banner from a previous attempt so a stale alpha-access
    // message can't linger across resubmits (e.g., after the account is activated).
    setEntitlementDenialMessage(null);
    setPerFileProgress({});
    // Set once the PDFs are up. A 400 after that is begin-processing's (no
    // files listed, malformed keys), not form validation.
    let processingRequested = false;

    try {
      // Step 1: create the Reports row server-side. Server generates `report_id`
      // and returns the S3 prefix (private/{identityId}/{reportId}/) to upload to.
      const { reportId, uploadPrefix } = await createReportMutation.mutateAsync({
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        year: new Date().getFullYear().toString(),
        file_type: 'inspection_report',
        contract_metadata: {
          contract_acceptance_date: formData.contractAcceptanceDate || '',
          inspection_window_days: formData.inspectionWindowDays || '0',
          is_as_is: formData.isAsIs.toString(),
          repair_credit_amount: formData.repairCreditAmount || '0',
          seller_repair_credit_agreed: formData.sellerRepairCreditAgreed.toString(),
          single_defect_threshold: formData.singleDefectThreshold || '0',
          category_threshold: formData.categoryThreshold || '0',
        },
      });

      // Step 2: upload PDFs as pure file transfer — no S3 object metadata.
      const uploadTask = uploadReports(formData.files, uploadPrefix, setPerFileProgress);
      uploadTaskRef.current = uploadTask;
      setUploadStarted(true);
      await uploadTask.result;
      uploadTaskRef.current = null;
      setUploadStarted(false);

      // Step 3: signal upload complete. The API lists the upload bucket
      // and copies each PDF to the organized bucket synchronously. The
      // bucket is strongly consistent for fresh PUTs, so a 408 only fires
      // when the count is short of expectedFileCount (silent upload failure
      // or stale frontend state). Retry once after 1.5s — if the upload
      // truly succeeded, the second call will see all the files. A 429 is
      // the gateway throttle during a burst; Lambda never ran, so nothing
      // changed server-side and one retry after Retry-After is safe. 400 and
      // 500 are not retried (they indicate a malformed key, an upload over the
      // limits, or a copy failure, none recoverable by sleeping).
      const expectedFileCount = formData.files.length;
      processingRequested = true;
      try {
        await beginProcessingMutation.mutateAsync({ reportId, expectedFileCount });
      } catch (err) {
        const status = err instanceof ApiError ? err.status : undefined;
        if (status === HTTP_STATUS.REQUEST_TIMEOUT || status === HTTP_STATUS.TOO_MANY_REQUESTS) {
          const base = (err instanceof ApiError && err.retryAfterMs) || 1500;
          // Jitter so a booth crowd throttled together does not retry
          // together. Downward-only, matching the backend retry jitter.
          const waitMs = base * (1 - 0.25 * Math.random());
          await new Promise(resolve => setTimeout(resolve, waitMs));
          await beginProcessingMutation.mutateAsync({ reportId, expectedFileCount });
        } else {
          throw err;
        }
      }

      // That charge spent a run. The account is cached for 30 seconds, so a
      // stale positive balance would otherwise keep the zero-runs card hidden.
      queryClient.invalidateQueries({ queryKey: accountKeys.summary() });

      setUploadedReportId(reportId);
      setIsPolling(true);
      setIsUploading(false);
      showSuccess('Report uploaded successfully! Processing will begin shortly.');
    } catch (error) {
      uploadTaskRef.current = null;
      setUploadStarted(false);
      const appError = parseError(error);
      if (appError.code === ERROR_CODES.UPLOAD_CANCELLED) {
        setWasCancelled(true);
        showWarning('Upload cancelled. Your form data has been preserved.');
      } else if (appError.code === ERROR_CODES.ENTITLEMENT_DENIED) {
        setEntitlementDenialMessage(appError.userMessage);
        queryClient.invalidateQueries({ queryKey: accountKeys.summary() });
      } else if (appError.code === ERROR_CODES.TERMS_REQUIRED) {
        // The server's terms gate (BE-HND-03), usually a tab left open across
        // a version bump. Nothing was charged and a refused begin-processing
        // leaves the report PENDING. recheckTerms reads the status, and
        // ProtectedRoute swaps this page for the prompt. It never throws.
        showError(appError.userMessage);
        void recheckTerms();
      } else if (processingRequested && appError.code === ERROR_CODES.VALIDATION_ERROR) {
        // parseApiError's 400 copy says "review the form", which is right for
        // createReport and wrong here: the form was accepted and the files
        // sent. Every begin-processing 400 comes before the flip and the
        // charge, and the report stays PENDING. The upload-limit 400
        // (BE-SHR-05) carries a code, parses to UPLOAD_LIMIT_EXCEEDED with
        // its own copy, and takes the last arm.
        showError(BEGIN_PROCESSING_REJECTED_MESSAGE);
      } else {
        showError(appError.userMessage);
      }
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (uploadTaskRef.current) {
      uploadTaskRef.current.cancel();
    }
  };

  const isFormValid = Boolean(
    formData.address &&
      formData.city &&
      formData.state &&
      formData.zip &&
      formData.files.length > 0
  );

  // Polling stopped without a settled report. Deliberately no way back to the
  // filled-in form: resubmitting it would create and charge a second report.
  if (pollFailure) {
    return (
      <PageLayout
        title="Upload Report"
        subtitle="Upload and process your property inspection reports with AI-powered analysis"
      >
        <div className="processing-overlay">
          <div className="processing-card">
            <h3>We stopped checking on your report</h3>
            <div className="alert alert-warning mt-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {pollFailure.message}{' '}
              {pollFailure.sessionExpired ? (
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => { void logout(); }}
                >
                  Sign in again
                </button>
              ) : (
                <Link to="/reports">Go to My Reports</Link>
              )}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show processing overlay when polling
  if (isPolling) {
    return (
      <PageLayout
        title="Upload Report"
        subtitle="Upload and process your property inspection reports with AI-powered analysis"
      >
        <div className="processing-overlay">
          <div className="processing-card">
            <div className="spinner"></div>
            <h3>Processing Your Report</h3>
            <p>Our AI is analyzing your inspection report...</p>
            <ProcessingStepper currentStage={reportStatus?.processingStage} />
            <p className="hint">This usually takes 3-5 minutes. You can wait here or check My Reports later.</p>
            <button
              className="btn btn-outline-primary"
              onClick={() => navigate('/reports')}
            >
              View All Reports
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Upload Report"
      subtitle="Upload and process your property inspection reports with AI-powered analysis"
    >
      {noRuns && (
        // role="status", not "alert": this is advice shown up front, and the
        // denial banner below is the one alert on the page.
        <div className="alert alert-info upload-no-runs" role="status">
          <i className="bi bi-info-circle-fill me-2"></i>
          {noRuns.message}{' '}
          <Link to="/account">{noRuns.linkText}</Link>
        </div>
      )}
      <form onSubmit={handleSubmit} className="form-card">
        {/* Document Upload Section */}
        <div className="card-section">
          <div className="section-header">
            <i className="bi bi-file-earmark-arrow-up section-icon"></i>
            <h2 className="section-title">Document Upload</h2>
          </div>

        <div
          className={`upload-zone ${isDragOver ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="upload-icon">
            <i className="bi bi-cloud-upload"></i>
          </div>
          <h5 className="upload-title">
            Select PDF Files
            <span className="badge bg-primary ms-2">PDF Only</span>
          </h5>
          <p className="upload-subtitle">
            Drag and drop one or more inspection reports or click to browse (Max{' '}
            {FILE_SIZE.MAX_UPLOAD_MB}MB per file, {FILE_SIZE.MAX_TOTAL_UPLOAD_MB}MB total,{' '}
            up to {FILE_SIZE.MAX_FILES_PER_REPORT} files)
          </p>
          <input
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileChange}
            className="file-input"
            id="fileInput"
            ref={fileInputRef}
          />
          <button
            type="button"
            onClick={handleFileButtonClick}
            className="file-label"
          >
            Choose Files
          </button>
          {formData.files.length > 0 && (
            <ul className="file-list">
              {formData.files.map((f) => {
                const pct = perFileProgress[f.name] ?? 0;
                return (
                  <li
                    key={`${f.name}-${f.size}`}
                    className="file-list-item"
                  >
                    <i className="bi bi-file-pdf file-list-icon" />
                    <span className="file-list-name">{f.name}</span>
                    <span className="file-list-size">
                      {(f.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                    {!isUploading && (
                      <button
                        type="button"
                        className="btn btn-sm btn-link file-list-remove"
                        onClick={() => handleRemoveFile(f.name, f.size)}
                        aria-label={`Remove ${f.name}`}
                      >
                        Remove
                      </button>
                    )}
                    {isUploading && (
                      <div
                        className="progress file-list-progress"
                        role="progressbar"
                        aria-label={`${f.name} upload progress`}
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div
                          className="progress-bar"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {isUploading && (
            <div className="upload-progress mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">
                  Uploading {formData.files.length}{' '}
                  {formData.files.length === 1 ? 'file' : 'files'}…
                </span>
                {uploadStarted && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleCancelUpload}
                    aria-label="Cancel upload"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}
          {wasCancelled && !isUploading && (
            <div className="alert alert-info mt-3">
              Upload was cancelled. You can try again when ready.
            </div>
          )}
          {entitlementDenialMessage && !isUploading && (
            <div className="alert alert-warning mt-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {entitlementDenialMessage}{' '}
              <Link to="/account">Go to your account</Link>
            </div>
          )}
        </div>
      </div>

      {/* Property Information Section */}
      <div className="card-section">
        <div className="section-header">
          <i className="bi bi-house section-icon"></i>
          <h2 className="section-title">Property Information</h2>
        </div>

        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id="address"
            name="address"
            placeholder="Enter property address"
            value={formData.address}
            onChange={handleInputChange}
            maxLength={200}
            required
          />
          <label htmlFor="address">Property Address</label>
        </div>

        <div className="form-floating">
          <input
            type="text"
            className="form-control"
            id="city"
            name="city"
            placeholder="Enter city"
            value={formData.city}
            onChange={handleInputChange}
            maxLength={100}
            required
          />
          <label htmlFor="city">City</label>
        </div>

        <div className="property-input-group">
          <div className="form-floating">
            <select
              className="form-select"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              required
            >
              <option value="">Choose state</option>
              {US_STATES.map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
            <label htmlFor="state">State</label>
          </div>
          <div className="form-floating">
            <input
              type="text"
              className="form-control"
              id="zip"
              name="zip"
              placeholder="Enter ZIP code"
              pattern="[0-9]{5}"
              value={formData.zip}
              onChange={handleInputChange}
              required
            />
            <label htmlFor="zip">ZIP Code</label>
          </div>
        </div>
      </div>

      {/* Contract Details Section */}
      <div className="card-section">
        <div className="section-header">
          <i className="bi bi-file-earmark-text section-icon"></i>
          <h2 className="section-title">Contract Details</h2>
          <span className="section-badge">Optional</span>
        </div>
        <p className="section-description">
          Add your purchase contract details to enable contract-based defect flagging.
        </p>

        <div className="property-input-group">
          <div className="form-floating">
            <input
              type="date"
              className="form-control"
              id="contractAcceptanceDate"
              name="contractAcceptanceDate"
              value={formData.contractAcceptanceDate}
              onChange={handleInputChange}
            />
            <label htmlFor="contractAcceptanceDate">Contract Acceptance Date</label>
          </div>
          <div className="form-floating">
            <input
              type="number"
              className="form-control"
              id="inspectionWindowDays"
              name="inspectionWindowDays"
              placeholder="Days"
              min="0"
              max="30"
              value={formData.inspectionWindowDays}
              onChange={handleInputChange}
            />
            <label htmlFor="inspectionWindowDays">Inspection Window (Days)</label>
          </div>
        </div>

        <div className="property-input-group">
          <div className="form-floating">
            <input
              type="number"
              className="form-control"
              id="singleDefectThreshold"
              name="singleDefectThreshold"
              placeholder="$0"
              min="0"
              step="100"
              value={formData.singleDefectThreshold}
              onChange={handleInputChange}
            />
            <label htmlFor="singleDefectThreshold">Single Defect Threshold ($)</label>
          </div>
          <div className="form-floating">
            <input
              type="number"
              className="form-control"
              id="categoryThreshold"
              name="categoryThreshold"
              placeholder="$0"
              min="0"
              step="100"
              value={formData.categoryThreshold}
              onChange={handleInputChange}
            />
            <label htmlFor="categoryThreshold">Category Threshold ($)</label>
          </div>
        </div>

        <div className="form-floating">
          <input
            type="number"
            className="form-control"
            id="repairCreditAmount"
            name="repairCreditAmount"
            placeholder="$0"
            min="0"
            step="100"
            value={formData.repairCreditAmount}
            onChange={handleInputChange}
          />
          <label htmlFor="repairCreditAmount">Pre-negotiated Repair Credit ($)</label>
        </div>

        <div className="checkbox-group">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="isAsIs"
              name="isAsIs"
              checked={formData.isAsIs}
              onChange={handleInputChange}
            />
            <label className="form-check-label" htmlFor="isAsIs">
              Property sold &quot;As-Is&quot;
            </label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="sellerRepairCreditAgreed"
              name="sellerRepairCreditAgreed"
              checked={formData.sellerRepairCreditAgreed}
              onChange={handleInputChange}
            />
            <label className="form-check-label" htmlFor="sellerRepairCreditAgreed">
              Seller agreed to repair credit
            </label>
          </div>
        </div>
      </div>

      {/* Submit Section */}
      <div className="submit-section">
        <button
          type="submit"
          className={`btn-submit ${isUploading ? 'loading' : ''}`}
          disabled={!isFormValid || isUploading}
        >
          {isUploading ? 'Uploading...' : 'Submit Report for Processing'}
        </button>
        <p className="submit-text">
          Your report will be processed using AI-powered analysis
        </p>
      </div>
      </form>
    </PageLayout>
  );
};

export default UploadForm;
